import { CircleCheck, ImageOff, PackageSearch } from 'lucide-react'
import { useMemo, useRef } from 'react'
import { z } from 'zod'

import type { MarketingScreenStateType } from '@/api/admin-marketing-content-types'
import { MarketingScreenState } from '@/api/admin-marketing-content-types'
import { Text } from '@/components/base/text'
import { useI18n } from '@/i18n/i18n-react'
import { clx } from '@/utils/clx'

// Hide native scrollbars while keeping the element scrollable (cross-browser).
const HIDE_SCROLLBAR = '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'

// Drag-to-scroll: press and drag to pan, like the app's touch scroll but for a mouse. Pans on
// whichever axes the element can scroll, so it works for the horizontal image strip and the
// vertical feed alike. Returns a ref for the scroll container plus handlers to spread onto it.
function useDragScroll() {
	const ref = useRef<HTMLDivElement>(null)
	const drag = useRef({ down: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 })

	const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
		const el = ref.current
		if (!el) return
		drag.current = {
			down: true,
			startX: e.clientX,
			startY: e.clientY,
			scrollLeft: el.scrollLeft,
			scrollTop: el.scrollTop
		}
	}
	const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
		const el = ref.current
		if (!el || !drag.current.down) return
		el.scrollLeft = drag.current.scrollLeft - (e.clientX - drag.current.startX)
		el.scrollTop = drag.current.scrollTop - (e.clientY - drag.current.startY)
	}
	const stop = () => {
		drag.current.down = false
	}

	return { ref, handlers: { onPointerDown, onPointerMove, onPointerUp: stop, onPointerLeave: stop } }
}

// Mirror of the customer app's zMarketingContentItem (customer-app/api/sales-channels-types.ts). The dashboard
// has no store-feed zod of its own, so we validate here with the same shape the app validates with:
// items that fail to parse are dropped from the preview, exactly like the app drops them at runtime.
// CUSTOM is intentionally excluded — the store feed never carries it; the app cannot render it.
// The promotion image embeds the full Asset (mirrors ProductImage.asset) rather than a
// url/width/height snapshot — only the fields the preview actually renders are validated here.
const zAssetVariant = z.object({ type: z.string(), url: z.string() })
const zAsset = z.object({
	blurhash: z.string().optional(),
	width: z.number().optional(),
	height: z.number().optional(),
	variants: z.array(zAssetVariant)
})

// Same order as customer-app/components/asset-image.tsx `VARIANT_PRIORITY`.
const PREVIEW_VARIANT_PRIORITY = ['SMALL', 'MEDIUM', 'THUMBNAIL', 'LARGE', 'ORIGINAL']
const pickPreviewAssetUrl = (asset: z.infer<typeof zAsset>): string | undefined => {
	for (const type of PREVIEW_VARIANT_PRIORITY) {
		const match = asset.variants.find(v => v.type === type)
		if (match) return match.url
	}
	return asset.variants[0]?.url
}

const zImage = z.object({
	asset: zAsset,
	maxWidthPercent: z.number().optional()
})

const zBanner = z.discriminatedUnion('type', [
	z.object({ type: z.literal('IMAGE'), image: zImage }),
	z.object({
		type: z.literal('TEXT_WITH_BACKGROUND'),
		text: z.string(),
		containerClassName: z.string().optional(),
		aspectRatio: z.number().optional(),
		textClassName: z.string().optional()
	})
])

const zBase = z.object({
	uuid: z.string(),
	title: z.string().optional(),
	showTitle: z.boolean().optional(),
	showMoreEnabled: z.boolean().optional(),
	classNames: z
		.object({
			top: z.string().optional(),
			bottom: z.string().optional(),
			left: z.string().optional(),
			right: z.string().optional()
		})
		.optional()
})

const zPreviewPromotion = z.intersection(
	zBase,
	z.discriminatedUnion('type', [
		z.object({ type: z.literal('BANNER'), banner: zBanner, link: z.any().optional().nullable() }),
		z.object({ type: z.literal('MODAL'), banner: zBanner }),
		z.object({
			type: z.literal('LIST'),
			thumbnails: z.object({
				containerAspectRatio: z.number().optional(),
				itemAspectRatio: z.number().optional(),
				items: z.array(z.object({ asset: zAsset }))
			})
		}),
		z.object({ type: z.literal('SEPARATOR'), containerClassName: z.string().optional() })
	])
)

type PreviewPromotion = z.infer<typeof zPreviewPromotion>
type PreviewBanner = z.infer<typeof zBanner>

// `type: 'CAMPAIGN_ZONE'` is a layout-only marker rendered as a labelled placeholder so marketers
// can see where active campaign content is injected at read time.
export type PreviewItem = { uuid?: string; type: string; data: any }

// The customer-app default primary color (customer-app/global.css --primary).
const PRIMARY = '#002bbe'

/**
 * Replica of the customer-app home feed (customer-app/app/(tabs)/(home)/index.tsx),
 * rendered from the live editor state so marketers see what shipping will look like.
 */
type PreviewRowItem = { kind: 'promo'; promo: PreviewPromotion } | { kind: 'zone'; uuid: string }

export function FeedPreview({ items }: { items: PreviewItem[] }) {
	const { L } = useI18n()
	const feedScroll = useDragScroll()

	// Flatten + validate the same way the store feed + app do: attach uuid/type onto data, then
	// keep only items that parse against the promotion shape. CAMPAIGN_ZONE markers bypass
	// validation and render as a labelled placeholder.
	const rows = useMemo<PreviewRowItem[]>(() => {
		const out: PreviewRowItem[] = []
		items.forEach((item, index) => {
			const uuid = item.uuid ?? `preview-${index}`
			if (item.type === 'CAMPAIGN_ZONE') {
				out.push({ kind: 'zone', uuid })
				return
			}
			const candidate = { ...(item.data ?? {}), uuid, type: item.type }
			const parsed = zPreviewPromotion.safeParse(candidate)
			if (parsed.success) out.push({ kind: 'promo', promo: parsed.data })
		})
		return out
	}, [items])

	return (
		<div className='flex flex-col items-center gap-3'>
			<div className='relative w-[320px] shrink-0 overflow-hidden rounded-[2.5rem] border-[10px] border-ui-bg-component bg-white shadow-elevation-card-rest'>
				<div className='flex h-6 items-center justify-center bg-white'>
					<div className='h-1.5 w-16 rounded-full bg-ui-bg-component' />
				</div>
				<div
					ref={feedScroll.ref}
					{...feedScroll.handlers}
					className={`h-[560px] cursor-grab select-none overflow-y-auto bg-white active:cursor-grabbing ${HIDE_SCROLLBAR}`}
				>
					{rows.length === 0 ? (
						<div className='flex h-full flex-col items-center justify-center gap-2 px-6 text-center'>
							<ImageOff className='size-8 text-ui-fg-muted' />
							<Text className='text-xs text-ui-fg-subtle'>{L.Marketing.Campaign.Preview.Empty()}</Text>
						</div>
					) : (
						rows.map(row =>
							row.kind === 'zone' ? (
								<div key={row.uuid} className='px-5 py-4'>
									<div className='flex items-center justify-center rounded-2xl border-2 border-dashed border-ui-border-interactive bg-ui-bg-subtle py-6'>
										<Text className='text-xs font-medium text-ui-fg-subtle'>
											{L.Marketing.Layout.CampaignZone()}
										</Text>
									</div>
								</div>
							) : (
								<PreviewRow key={row.promo.uuid} promo={row.promo} width={300} />
							)
						)
					)}
				</div>
			</div>
			<Text className='max-w-[320px] text-center text-[11px] text-ui-fg-muted'>
				{L.Marketing.Campaign.Preview.Disclaimer()}
			</Text>
		</div>
	)
}

// The first renderable IMAGE banner in a feed - the checkout screen shows one full-bleed image
// (or crossfades the few it has), unlike the vertical, multi-row customer-app feed.
function firstImageUrl(items: PreviewItem[]): string | undefined {
	for (const item of items) {
		if (item.type !== 'BANNER') continue
		const candidate = { ...(item.data ?? {}), uuid: item.uuid ?? '', type: item.type }
		const parsed = zPreviewPromotion.safeParse(candidate)
		if (parsed.success && parsed.data.type === 'BANNER' && parsed.data.banner.type === 'IMAGE') {
			const url = pickPreviewAssetUrl(parsed.data.banner.image.asset)
			if (url) return url
		}
	}
	return undefined
}

/**
 * Replica of the merchant-app checkout (self-checkout exit) screen for a single screen state. A
 * landscape frame showing the state's first image full-bleed. A background replaces the built-in
 * status message, so the bag-check and cleared states only show their icon + message when no
 * background is set, mirroring the device's fallback.
 */
export function CheckoutScreenPreview({
	items,
	screenState
}: {
	items: PreviewItem[]
	screenState: MarketingScreenStateType
}) {
	const { L } = useI18n()
	const bgUrl = useMemo(() => firstImageUrl(items), [items])

	let overlay: { Icon: typeof PackageSearch; title: string; body: string } | null = null
	if (!bgUrl && screenState === MarketingScreenState.CheckoutBagCheck) {
		overlay = {
			Icon: PackageSearch,
			title: L.Marketing.CheckoutPreview.BagCheckTitle(),
			body: L.Marketing.CheckoutPreview.BagCheckBody()
		}
	} else if (!bgUrl && screenState === MarketingScreenState.CheckoutCleared) {
		overlay = {
			Icon: CircleCheck,
			title: L.Marketing.CheckoutPreview.ClearedTitle(),
			body: L.Marketing.CheckoutPreview.ClearedBody()
		}
	}

	return (
		<div className='flex flex-col items-center gap-3'>
			<div
				className='relative aspect-[16/10] w-[320px] overflow-hidden rounded-xl border border-ui-border-base shadow-elevation-card-rest'
				style={{ backgroundColor: PRIMARY }}
			>
				{bgUrl && (
					<img src={bgUrl} alt='' draggable={false} className='absolute inset-0 h-full w-full object-cover' />
				)}
				{!bgUrl && !overlay && (
					<div className='absolute inset-0 flex items-center justify-center'>
						<ImageOff className='size-8 text-white/60' />
					</div>
				)}
				{overlay && (
					<div className='absolute inset-0 flex flex-col items-center justify-center gap-2.5 px-6 text-center'>
						<div className='rounded-full bg-white/15 p-4'>
							<overlay.Icon className='size-10 text-white' />
						</div>
						<Text className='text-lg font-bold text-white'>{overlay.title}</Text>
						<Text className='text-sm text-white/90'>{overlay.body}</Text>
					</div>
				)}
			</div>
			<Text className='max-w-[320px] text-center text-[11px] text-ui-fg-muted'>
				{L.Marketing.CheckoutPreview.Disclaimer()}
				{overlay && ` ${L.Marketing.CheckoutPreview.FallbackHint()}`}
			</Text>
		</div>
	)
}

function SectionWrapper({ promo, children }: { promo: PreviewPromotion; children: React.ReactNode }) {
	return (
		<div className={clx('flex flex-col justify-center py-8', promo.classNames?.top, promo.classNames?.bottom)}>
			{promo.showTitle && promo.title && (
				<div
					className={clx(
						'flex flex-row items-center justify-between px-6 pb-2',
						promo.classNames?.left,
						promo.classNames?.right
					)}
				>
					<span className='text-left text-base font-semibold' style={{ color: PRIMARY }}>
						{promo.title}
					</span>
				</div>
			)}
			{children}
		</div>
	)
}

function PreviewRow({ promo, width }: { promo: PreviewPromotion; width: number }) {
	if (promo.type === 'SEPARATOR') {
		return (
			<div className='px-10'>
				<div className='h-px w-full' style={{ backgroundColor: 'rgba(0,0,0,0.2)' }} />
			</div>
		)
	}

	if (promo.type === 'BANNER' || promo.type === 'MODAL') {
		return (
			<SectionWrapper promo={promo}>
				<BannerView banner={promo.banner} width={width} />
			</SectionWrapper>
		)
	}

	if (promo.type === 'LIST') {
		return (
			<SectionWrapper promo={promo}>
				<ListStrip thumbnails={promo.thumbnails} width={width} />
			</SectionWrapper>
		)
	}

	return null
}

function ListStrip({
	thumbnails,
	width
}: {
	thumbnails: Extract<PreviewPromotion, { type: 'LIST' }>['thumbnails']
	width: number
}) {
	const { ref, handlers } = useDragScroll()
	const contentWidth = width - 48
	const containerAspect = thumbnails.containerAspectRatio ?? 2.5
	const itemAspect = thumbnails.itemAspectRatio ?? 1
	const stripHeight = contentWidth / containerAspect
	const itemWidth = stripHeight * itemAspect
	return (
		<div
			ref={ref}
			{...handlers}
			className={`flex cursor-grab select-none gap-4 overflow-x-auto px-6 active:cursor-grabbing ${HIDE_SCROLLBAR}`}
			style={{ height: stripHeight }}
		>
			{thumbnails.items.map((thumb, i) => {
				const url = pickPreviewAssetUrl(thumb.asset)
				return (
					<div
						key={i}
						className='shrink-0 overflow-hidden rounded-2xl bg-ui-bg-subtle'
						style={{ height: stripHeight, width: itemWidth }}
					>
						{url ? (
							<img src={url} alt='' draggable={false} className='h-full w-full object-contain' />
						) : (
							<PlaceholderBox />
						)}
					</div>
				)
			})}
		</div>
	)
}

function BannerView({ banner, width }: { banner: PreviewBanner; width: number }) {
	if (banner.type === 'TEXT_WITH_BACKGROUND') {
		const aspect = banner.aspectRatio ?? 2
		return (
			<div className='px-6' style={{ aspectRatio: String(aspect) }}>
				<div
					className='flex h-full w-full items-center justify-center rounded-2xl'
					style={{ backgroundColor: PRIMARY }}
				>
					<span className='px-4 text-center text-2xl font-bold leading-9 text-white'>{banner.text}</span>
				</div>
			</div>
		)
	}

	const image = banner.image
	const pct = (image?.maxWidthPercent ?? 90) / 100
	const imgWidth = Math.floor(width * pct)
	const asset = image?.asset
	const aspect = asset?.width && asset?.height ? asset.width / asset.height : 2
	const url = asset ? pickPreviewAssetUrl(asset) : undefined
	return (
		<div className='px-6'>
			<div className='overflow-hidden rounded-2xl bg-ui-bg-subtle' style={{ width: imgWidth }}>
				{url ? (
					<img src={url} alt='' className='w-full object-contain' style={{ aspectRatio: String(aspect) }} />
				) : (
					<div style={{ aspectRatio: String(aspect) }}>
						<PlaceholderBox />
					</div>
				)}
			</div>
		</div>
	)
}

function PlaceholderBox() {
	return (
		<div className='flex h-full w-full items-center justify-center bg-ui-bg-subtle'>
			<ImageOff className='size-5 text-ui-fg-muted' />
		</div>
	)
}
